import React, { useEffect, useRef, useState } from 'react';
import {
  buildCrabThumbnailUrl,
  buildDocumentThumbnailSrc,
  buildProxiedThumbnailSrc,
  getCrabThumbnailHeaders,
  getTemporaryThumbnailOverride,
} from './thumbnailUrl';

/**
 * Loads a document thumbnail: explicit API URLs, then same-origin Portunus /pub/.../thumbnail
 * proxy, then client CRAB fetch + headers, then legacy query URL if REACT_APP_THUMBNAIL_LEGACY=1.
 *
 * @param {Record<string, unknown>} doc
 * @param {Record<string, unknown>} payload
 * @param {string} [preset] — CRAB preset (gallery_thumb, document_thumb, …)
 * @param {string} [alt]
 * @param {string} [className]
 */
export function ThumbnailImage({ doc, payload, preset, alt, className }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const blobRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    function setIfAlive(next) {
      if (!cancelled) setSrc(next);
    }

    function fail() {
      if (!cancelled) setFailed(true);
    }

    async function load() {
      if (!doc || typeof doc !== 'object' || !payload) {
        fail();
        return;
      }

      const forced = getTemporaryThumbnailOverride();
      if (forced) {
        setIfAlive(forced);
        return;
      }

      if (typeof doc.thumbnailUrl === 'string' && doc.thumbnailUrl.trim() !== '') {
        setIfAlive(doc.thumbnailUrl.trim());
        return;
      }
      if (typeof doc.url === 'string' && doc.url.trim() !== '') {
        setIfAlive(doc.url.trim());
        return;
      }
      if (typeof doc.publicUrl === 'string' && doc.publicUrl.trim() !== '') {
        setIfAlive(doc.publicUrl.trim());
        return;
      }

      const proxied = buildProxiedThumbnailSrc(doc, preset);
      if (proxied) {
        setIfAlive(proxied);
        return;
      }

      const headers = getCrabThumbnailHeaders(payload);
      const crabUrl = buildCrabThumbnailUrl(doc, preset);
      if (headers && crabUrl) {
        try {
          const res = await fetch(crabUrl, { headers });
          if (!res.ok) throw new Error(String(res.status));
          const blob = await res.blob();
          if (cancelled) return;
          if (blobRef.current) URL.revokeObjectURL(blobRef.current);
          blobRef.current = URL.createObjectURL(blob);
          setIfAlive(blobRef.current);
        } catch {
          fail();
        }
        return;
      }

      const legacy = buildDocumentThumbnailSrc(doc, payload);
      if (legacy) {
        setIfAlive(legacy);
        return;
      }

      fail();
    }

    setFailed(false);
    setSrc(null);
    load();

    return () => {
      cancelled = true;
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, [doc, payload, preset]);

  if (failed || !src) return null;

  return (
    <img src={src} alt={alt || ''} className={className} loading="lazy" decoding="async" />
  );
}
